import React from "react";

/**
 * QuestionNavigator Component
 * Navigation controls for questions with submit button
 */
const QuestionNavigator = ({
  currentIndex,
  totalQuestions,
  answeredCount,
  answers,
  questions,
  onPrevious,
  onNext,
  onGoToQuestion,
  onSubmit,
  allAnswered,
  isSubmitting,
}) => {
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < totalQuestions - 1;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
      {/* Question Grid */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
          Question Overview
        </h3>
        <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2">
          {questions.map((q, index) => {
            const isAnswered = answers[q.questionId] !== undefined && answers[q.questionId] !== null && answers[q.questionId] !== "";
            const isCurrent = index === currentIndex;

            return (
              <button
                key={q.questionId}
                onClick={() => onGoToQuestion(index)}
                className={`aspect-square rounded-lg font-bold text-sm transition-all duration-200 ${
                  isCurrent
                    ? "bg-indigo-600 text-white ring-2 ring-indigo-300 dark:ring-indigo-500 scale-110"
                    : isAnswered
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
                title={`Question ${index + 1}${isAnswered ? " (Answered)" : ""}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {/* Previous Button */}
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className={`flex-1 sm:flex-initial px-6 py-3 font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
            canGoPrevious
              ? "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
              : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Previous
        </button>

        {/* Progress Info */}
        <div className="flex-1 text-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {answeredCount} of {totalQuestions} answered
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Next or Submit Button */}
        {canGoNext ? (
          <button
            onClick={onNext}
            className="flex-1 sm:flex-initial px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
          >
            Next
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        ) : (
          <button
            onClick={onSubmit}
            disabled={isSubmitting}
            className={`flex-1 sm:flex-initial px-8 py-3 font-bold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
              isSubmitting
                ? "bg-gray-400 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                : allAnswered
                ? "bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                : "bg-yellow-500 hover:bg-yellow-600 text-white"
            }`}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Submit {!allAnswered && `(${answeredCount}/${totalQuestions})`}
              </>
            )}
          </button>
        )}
      </div>

      {/* Warning if not all answered */}
      {!allAnswered && currentIndex === totalQuestions - 1 && (
        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <h4 className="font-bold text-yellow-800 dark:text-yellow-300 mb-1">
                Some questions are unanswered
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                You have {totalQuestions - answeredCount} unanswered question(s). You can still submit, but consider reviewing all questions first.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionNavigator;


